import Img from 'src/components/shared/Img'
import PropTypes from 'prop-types'
import { resolveContentfulImageSrc } from 'src/common/resolveContentfulImageSrc'
import {
  trustMarkerEntryAlt,
  trustMarkerEntryClickPayload,
  trustMarkerEntryColor,
  trustMarkerEntryId,
  trustMarkerEntryImage,
  trustMarkerEntryLink,
  trustMarkerEntryLogo,
  trustMarkerEntryRating,
  trustMarkerEntryTotalReview,
} from 'src/common/trustMarkerEntryFields'
import { useIntersectionObserver } from 'src/common/hooks/useIntersectionObserver'
import { useAnalytics } from 'src/common/analytics'
import ButtonLink from 'src/components/shared/ButtonLink'
import { AwardComponent } from '../Awards'

import DynamicMarkers from './DynamicMarkers'

/**
 * Enhanced TrustMarkers component with improved logosGroup layout
 * - Desktop: More spacing between elements, reduced width
 * - Mobile: 2x2 grid layout for logosGroup items
 */
const TrustMarkersWithLogosGrid = ({
  trustMarkersCombination,
  isDynamic = false,
  isFullLink = true,
  logosGroup,
  isFooter = true,
  statusCode,
}) => {
  const { track } = useAnalytics()
  const { ref } = useIntersectionObserver({
    threshold: 0.5,
    onChange: (isIntersecting) => {
      if (!isIntersecting) return
      track(
        {
          additional_info: {
            image: 'trust-marker-' + trustMarkersCombination?.id,
          },
          event_type: 'track',
        },
        {},
        'section_view'
      )
    },
    freezeOnceVisible: true,
  })

  const handleClick = ({ trustMarker, i }) => {
    track(
      {
        event_type: 'track',
        click_id: trustMarker.id,
        click_position: i,
        click_type: 'Link Click',
        additional_info: {
          section: 'trust-marker-' + trustMarker.id,
          click_url: trustMarker.link,
        },
      },
      {},
      'button_click'
    )
  }

  const hasLogosGroup =
    logosGroup && Array.isArray(logosGroup) && logosGroup.length > 0

  const markers = (trustMarkersCombination?.trustMarker ?? []).filter((tm) =>
    isDynamic
      ? resolveContentfulImageSrc(
          trustMarkerEntryLogo(tm) || trustMarkerEntryImage(tm)
        )
      : resolveContentfulImageSrc(trustMarkerEntryImage(tm))
  )

  return (
    <div
      ref={ref}
      className="flex flex-col py-6 lg:flex-row lg:flex-nowrap lg:items-center lg:justify-center"
    >
      {/* Render trust markers */}
      <div className="flex flex-col items-center justify-center lg:shrink-0 lg:flex-row lg:flex-nowrap lg:gap-0">
        {markers.map((trustMarker, i) => {
          const id = trustMarkerEntryId(trustMarker)
          const link = trustMarkerEntryLink(trustMarker)
          const clickPayload = trustMarkerEntryClickPayload(trustMarker)

          return (
            <div
              key={id}
              className="w-full border-b border-gray-270 py-6 lg:flex lg:h-24 lg:w-fit lg:items-center lg:border-r lg:border-b-0 lg:px-2 lg:py-0"
            >
              {isFullLink && (
                <ButtonLink
                  href={link}
                  className="relative flex items-center justify-center"
                  disabled={!link}
                  onClick={() => handleClick({ i, trustMarker: clickPayload })}
                  target="blank"
                >
                  {isDynamic && (
                    <DynamicMarkers
                      id={id}
                      alt={trustMarkerEntryAlt(trustMarker)}
                      image={
                        trustMarkerEntryLogo(trustMarker) ||
                        trustMarkerEntryImage(trustMarker)
                      }
                      rating={Number(trustMarkerEntryRating(trustMarker))}
                      totalReviews={Number(
                        trustMarkerEntryTotalReview(trustMarker)
                      )}
                      color={trustMarkerEntryColor(trustMarker)}
                    />
                  )}
                  {!isDynamic && (
                    <Img
                      src={resolveContentfulImageSrc(
                        trustMarkerEntryImage(trustMarker)
                      )}
                      alt={trustMarkerEntryAlt(trustMarker)}
                      width={300}
                      height={300}
                      className="h-20"
                      imgClassName="object-contain"
                      layout="intrinsic"
                    />
                  )}
                </ButtonLink>
              )}
              {!isFullLink && (
                <div className="min-w-36 relative flex items-center justify-center">
                  {isDynamic && (
                    <DynamicMarkers
                      id={id}
                      alt={trustMarkerEntryAlt(trustMarker)}
                      image={
                        trustMarkerEntryLogo(trustMarker) ||
                        trustMarkerEntryImage(trustMarker)
                      }
                      rating={Number(trustMarkerEntryRating(trustMarker))}
                      totalReviews={Number(
                        trustMarkerEntryTotalReview(trustMarker)
                      )}
                      color={trustMarkerEntryColor(trustMarker)}
                      link={link}
                      onClick={() =>
                        handleClick({ i, trustMarker: clickPayload })
                      }
                    />
                  )}
                  {!isDynamic && (
                    <Img
                      src={resolveContentfulImageSrc(
                        trustMarkerEntryImage(trustMarker)
                      )}
                      alt={trustMarkerEntryAlt(trustMarker)}
                      width={300}
                      height={300}
                      className="h-20"
                      imgClassName="object-contain"
                      layout="intrinsic"
                    />
                  )}
                </div>
              )}
            </div>
          )
        })}
      </div>

      {/* Render logos group with enhanced layout */}
      {hasLogosGroup && (
        <div className="w-full shrink-0 lg:ml-4 lg:w-auto">
          {/* Mobile: single column layout with bottom border - centered */}
          <div className="flex flex-col items-center justify-center lg:hidden">
            {logosGroup.map((item, index) => {
              const Component = item.component
              const isLast = index === logosGroup.length - 1

              return (
                <div
                  key={item.id}
                  className={`flex w-full items-center justify-center py-6 ${
                    !isLast ? 'border-b border-gray-270' : ''
                  }`}
                >
                  {Component ? (
                    <Component
                      item={item}
                      isFooter={isFooter}
                      statusCode={statusCode}
                    />
                  ) : (
                    <AwardComponent
                      item={item}
                      isFooter={isFooter}
                      statusCode={statusCode}
                    />
                  )}
                </div>
              )
            })}
          </div>

          {/* Desktop: Horizontal layout with right border */}
          <div className="hidden lg:flex lg:flex-row lg:flex-nowrap lg:items-center lg:justify-center">
            {logosGroup.map((item, index) => {
              const Component = item.component
              const isLast = index === logosGroup.length - 1

              return (
                <div
                  key={item.id}
                  className={`flex h-24 items-center justify-center ${
                    !isLast ? 'border-r border-gray-270' : ''
                  }`}
                >
                  {Component ? (
                    <Component
                      item={item}
                      isFooter={isFooter}
                      statusCode={statusCode}
                    />
                  ) : (
                    <AwardComponent
                      item={item}
                      isFooter={isFooter}
                      statusCode={statusCode}
                    />
                  )}
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

TrustMarkersWithLogosGrid.propTypes = {
  trustMarkersCombination: PropTypes.object,
  isDynamic: PropTypes.bool,
  isFullLink: PropTypes.bool,
  logosGroup: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      img: PropTypes.string,
      url: PropTypes.string,
      widget: PropTypes.object,
      alt: PropTypes.string,
      component: PropTypes.elementType,
    })
  ),
  isFooter: PropTypes.bool,
  statusCode: PropTypes.number,
  mobileLogosTitle: PropTypes.string,
}

export default TrustMarkersWithLogosGrid
